import React, {useMemo} from 'react';
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {makeStyles, createStyles} from "@material-ui/core/styles";
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles((theme) =>
    createStyles({
        filterPaper: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        searchButton: {
            height: '100%',
            minHeight: 40,
        },
    })
);

const GlbReceitasFilterBar = ({
    categorias,
    glbModelos,
    glbMedidas,
    glbBandas,
    filters,
    onFilterChange,
    onSearch,
    isLoading
}) => {
    const classes = useStyles();

    // Extrair fabricantes distintos dos modelos globais
    const fabricantes = useMemo(() => {
        if (!glbModelos || glbModelos.length === 0) return [];
        const fabMap = {};
        glbModelos.forEach(m => {
            if (m.glbFabricanteId && !fabMap[m.glbFabricanteId]) {
                fabMap[m.glbFabricanteId] = {
                    id: m.glbFabricanteId,
                    descricao: m.glbFabricante ? m.glbFabricante.descricao : 'Fab #' + m.glbFabricanteId
                };
            }
        });
        return Object.values(fabMap).sort((a, b) => (a.descricao || '').localeCompare(b.descricao || ''));
    }, [glbModelos]);

    // Filtrar modelos por fabricante selecionado
    const modelosFiltrados = useMemo(() => {
        if (!glbModelos) return [];
        if (!filters.fabricanteId) return glbModelos;
        return glbModelos.filter(m => m.glbFabricanteId === filters.fabricanteId);
    }, [glbModelos, filters.fabricanteId]);

    return (
        <Paper className={classes.filterPaper}>
            <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        id="categoria-selector"
                        options={categorias || []}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option && option.descricao ? option.descricao : '';
                        }}
                        value={categorias.find(c => c.id === filters.categoriaId) || null}
                        onChange={(e, newValue) => {
                            onFilterChange('categoriaId', newValue ? newValue.id : null);
                        }}
                        renderOption={(option) => option.descricao || ''}
                        renderInput={(params) => (
                            <TextField {...params} label="Categoria" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        id="glb-fabricante-selector"
                        options={fabricantes}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option && option.descricao ? option.descricao : '';
                        }}
                        value={fabricantes.find(f => f.id === filters.fabricanteId) || null}
                        onChange={(e, newValue) => {
                            onFilterChange('fabricanteId', newValue ? newValue.id : null);
                        }}
                        renderOption={(option) => option.descricao || ''}
                        renderInput={(params) => (
                            <TextField {...params} label="Fabricante" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        id="glb-modelo-selector"
                        options={modelosFiltrados}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option && option.descricao ? option.descricao : '';
                        }}
                        value={modelosFiltrados.find(m => m.id === filters.modeloId) || null}
                        onChange={(e, newValue) => {
                            onFilterChange('modeloId', newValue ? newValue.id : null);
                        }}
                        renderOption={(option) => option.descricao || ''}
                        renderInput={(params) => (
                            <TextField {...params} label="Modelo" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        id="glb-medida-selector"
                        options={glbMedidas || []}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option && option.descricao ? option.descricao : '';
                        }}
                        value={(glbMedidas || []).find(m => m.id === filters.medidaId) || null}
                        onChange={(e, newValue) => {
                            onFilterChange('medidaId', newValue ? newValue.id : null);
                        }}
                        renderOption={(option) => option.descricao || ''}
                        renderInput={(params) => (
                            <TextField {...params} label="Medida" />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <Autocomplete
                        id="glb-banda-selector"
                        options={glbBandas || []}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option && option.descricao ? option.descricao : '';
                        }}
                        value={(glbBandas || []).find(b => b.id === filters.bandaId) || null}
                        onChange={(e, newValue) => {
                            onFilterChange('bandaId', newValue ? newValue.id : null);
                        }}
                        renderOption={(option) => option.descricao || ''}
                        renderInput={(params) => (
                            <TextField {...params} label="Banda" />
                        )}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2} lg={1}>
                    <TextField
                        label="Vida Pneu"
                        type="number"
                        fullWidth
                        value={filters.vidaPneuRaspagem}
                        onChange={(e) => onFilterChange('vidaPneuRaspagem', e.target.value)}
                        InputProps={{ inputProps: { min: 0 } }}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={1} lg={1}>
                    {isLoading ? (
                        <CircularProgress size={36} />
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.searchButton}
                            disabled={false}
                            onClick={onSearch}
                            startIcon={<SearchIcon />}
                        >
                            Buscar
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};

export default GlbReceitasFilterBar;
